/* @flow */

import React, { PureComponent } from "react";
import { ValidationError } from "yup";
import { DDAFeeAccountInformationSchema } from "../../../validators/schemas/ddaFeeAccount";
import { createEmptyDDAEntry } from "../DDAFeeAccountHelper";
import type {
  FormBlurHandlerEventArg,
  FormChangeHandlerEventArg,
  Action,
  DDAEntry,
  UIState,
  ValidationErrors
} from "../../../types";
import TextField from "../../TextField";
import Fieldset from "../../Fieldset";
import Grid from "../../Grid";
import { Whole, Third } from "../../GridItem";
import Button from "../../Button";
import { connect } from "react-redux";
import { unflattenKeys } from "../../../collection/index";
import styles from "./DDAText.scss";

type DDAFeeAccountInformationFormProps = {
  ddaEntry: DDAEntry,
  onSuccess: (fields: DDAEntry) => void,
  ui: UIState,
  forwardedRef?: Object,
  dispatch: Action => Action,
  business: BusinessState
};

type DDAFeeAccountInformationFormState = {
  errors: ValidationErrors,
  values: DDAEntry
};

export class DDAFeeAccountInformationForm extends PureComponent<
  DDAFeeAccountInformationFormProps,
  DDAFeeAccountInformationFormState
> {
  constructor(props: DDAFeeAccountInformationFormProps) {
    super(props);

    const ddaEntry = props.ddaEntry || createEmptyDDAEntry();
    this.state = {
      values: ddaEntry,
      errors: {}
    };

    if (props.forwardedRef) {
      props.forwardedRef.current = this;
    }
  }

  handleComponentBlur = (event: FormBlurHandlerEventArg) => {
    const { name } = event.currentTarget;
    let error;
    try {
      DDAFeeAccountInformationSchema.validateSyncAt(name, this.state.values);
    } catch (exception) {
      if (ValidationError.isError(exception)) {
        error = exception.message;
      } else {
        throw exception;
      }
    }

    this.setState(prevState => ({
      errors: {
        ...prevState.errors,
        [name]: error
      }
    }));
  };

  handleComponentChange = (event: FormChangeHandlerEventArg) => {
    const { name, value } = event.currentTarget;
    this.setState(prevState => ({
      values: {
        ...prevState.values,
        [name]: value
      }
    }));

    if (!this.props.ui.ddaEditInProgress) {
      this.props.dispatch({ type: "UI_DDA_EDIT_START" });
    }
  };

  handleFormKeydown = (
    event: SyntheticKeyboardEvent<
      HTMLInputElement | HTMLSelectElement | HTMLButtonElement
    >
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      this.validateAndSubmit();
    }
  };

  handleFormButtonClick = event => {
    event.preventDefault();
    this.validateAndSubmit(true);
  };

  validateAndSubmit = (shouldSubmit: boolean = true) => {
    try {
      DDAFeeAccountInformationSchema.validateSync(this.state.values, {
        abortEarly: false
      });

      this.setState({ errors: {} });

      if (shouldSubmit && this.props.onSuccess) {
        this.props.onSuccess(this.state.values);
      }
      return true;
    } catch (exception) {
      if (ValidationError.isError(exception)) {
        // Fix: Add empty object as initial value for reduce
        const errorObj = exception.inner.reduce(
          (accumulator, error) => {
            accumulator[error.params.path] = error.message;
            return accumulator;
          },
          {} // Initialize with an empty object
        );

        this.setState({
          errors: unflattenKeys(errorObj)
        });

        return false;
      }
      throw exception;
    }
  };

  populateBusinessAddress = () => {
    const { business } = this.props;
    if (!business || !business.address || !business.address.values) {
      return;
    }

    const address = business.address.values;
    const addressLine2 = address.extended_address
      ? `, ${address.extended_address}`
      : "";
    let final_address = `${address.street_address}${addressLine2}, ${address.region}, ${address.postal_code}`;
    final_address = final_address === ", , " ? "" : final_address;
    if (final_address) {
      this.setState(prevState => ({
        values: {
          ...prevState.values,
          address: final_address
        }
      }));
    }
  };

  populateBusinessFields = () => {
    const { business } = this.props;
    const updates = {};
    if (business.values.legal_name) {
      updates.businessName = business.values.legal_name;
    }
    if (business.values.phone) {
      updates.phone = business.values.phone;
    }

    if (Object.keys(updates).length > 0) {
      this.setState(prevState => ({
        values: {
          ...prevState.values,
          ...updates
        }
      }));
    }
    this.populateBusinessAddress();
  };

  componentDidMount() {
    this.populateBusinessFields();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.business !== this.props.business) {
      this.populateBusinessFields();
    }
  }

  render() {
    const { errors, values } = this.state;
    const { ui = {} } = this.props;
    return (
      <React.Fragment>
        <form
          onKeyDown={this.handleFormKeydown}
          data-test-id="DDAFeeAccountInformationForm">
          <Fieldset title={`Sign the Direct Debit Agreement`}>
            <Grid>
              <Whole>
                <h3 className={styles.heading}>Direct Debit Bank Account</h3>
              </Whole>
              <Whole>
                <TextField
                  errorText={errors.businessName}
                  labelText="Business Name"
                  name="businessName"
                  onBlur={this.handleComponentBlur}
                  onChange={this.handleComponentChange}
                  placeholder="Business Name"
                  value={values.businessName}
                />
              </Whole>
              <Whole>
                <TextField
                  errorText={errors.address}
                  labelText="Address of Business"
                  name="address"
                  onBlur={this.handleComponentBlur}
                  onChange={this.handleComponentChange}
                  placeholder="Address of Business"
                  value={values.address}
                />
              </Whole>
              <Whole>
                <TextField
                  errorText={errors.phone}
                  labelText="Contact Phone Number"
                  name="phone"
                  onBlur={this.handleComponentBlur}
                  onChange={this.handleComponentChange}
                  placeholder="00 0000 0000"
                  value={values.phone}
                />
              </Whole>
              <Third>
                <TextField
                  errorText={errors.bankName}
                  labelText="Bank Name"
                  name="bankName"
                  onBlur={this.handleComponentBlur}
                  onChange={this.handleComponentChange}
                  placeholder="Bank Name"
                  value={values.bankName}
                />
              </Third>
              <Third>
                <TextField
                  errorText={errors.bsbNumber}
                  labelText="BSB Number"
                  name="bsbNumber"
                  onBlur={this.handleComponentBlur}
                  onChange={this.handleComponentChange}
                  placeholder="BSB Number"
                  value={values.bsbNumber}
                />
              </Third>
              <Third>
                <TextField
                  errorText={errors.accountNumber}
                  labelText="Account Number"
                  name="accountNumber"
                  onBlur={this.handleComponentBlur}
                  onChange={this.handleComponentChange}
                  placeholder="Account Number"
                  value={values.accountNumber}
                />
              </Third>
              <Whole>
                <p className={styles.subHeading}>
                  This can be the same as the disbursement account or different
                </p>
              </Whole>
              <Whole>
                <h3 className={styles.heading}>Signature</h3>
              </Whole>
              <Whole>
                <TextField
                  errorText={errors.accountHolderName}
                  labelText="Full Name"
                  name="accountHolderName"
                  onBlur={this.handleComponentBlur}
                  onChange={this.handleComponentChange}
                  placeholder="Full Name"
                  value={values.accountHolderName}
                />
              </Whole>
              <Whole>
                <TextField
                  errorText={errors.accountHolderTitle}
                  labelText="Job Title"
                  name="accountHolderTitle"
                  onBlur={this.handleComponentBlur}
                  onChange={this.handleComponentChange}
                  placeholder="Job Title"
                  value={values.accountHolderTitle}
                />
              </Whole>
            </Grid>
            <div className={styles.buttonDivider}>
              <Button
                intent="primary"
                fullWidth={true}
                disabled={ui.asyncActionInFlight}
                data-test-id="dda-done-button"
                onClick={this.handleFormButtonClick}>
                Done
              </Button>
            </div>
          </Fieldset>
        </form>
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => {
  const { ui, business } = state;
  return {
    ui,
    business
  };
};

export default connect(mapStateToProps)(DDAFeeAccountInformationForm);
